import React, { useState, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { optimizeImage } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, X, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user, setUser } = useAuth();
  const logoInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [publicName, setPublicName] = useState(user?.public_name ?? user?.name ?? "");
  const [brandColor, setBrandColor] = useState(user?.brand_color || "#059669");
  const [brandLogo, setBrandLogo] = useState(user?.brand_logo_url || "");
  const [currency, setCurrency] = useState(user?.currency || "₡");
  const [paymentInfo, setPaymentInfo] = useState(user?.payment_info || "");
  const [sinpeNumber, setSinpeNumber] = useState(user?.sinpe_number || "");
  const [whatsapp, setWhatsapp] = useState(user?.whatsapp || "");
  const [saving, setSaving] = useState(false);

  const handleLogoUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadingLogo(true);
    try {
      const optimized = await optimizeImage(f);
      const fd = new FormData();
      fd.append("file", optimized);
      const res = await api.post("/raffles/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBrandLogo(BACKEND_URL + res.data.url);
      toast.success("Logo subido");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Error al subir el logo");
    } finally { setUploadingLogo(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/settings", { public_name: publicName, brand_color: brandColor, brand_logo_url: brandLogo, currency, payment_info: paymentInfo, sinpe_number: sinpeNumber, whatsapp });
      setUser(res.data);
      toast.success("Marca actualizada");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <DashboardLayout title="Marca">
      <div className="max-w-xl bg-white rounded-2xl border border-neutral-200 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <h2 className="font-display font-bold text-lg mb-1">Personalización y pagos</h2>
        <p className="text-sm text-neutral-500 mb-6">Se aplica a las páginas públicas y a los correos de tus rifas.</p>
        <div data-testid="brand-preview" className="rounded-xl border border-neutral-200 overflow-hidden mb-6">
          <div className="px-4 py-3 flex items-center gap-3" style={{ background: brandColor }}>
            {brandLogo ? (
              <img src={brandLogo} alt="logo" className="h-9 w-auto max-w-[140px] object-contain bg-white/90 rounded px-1.5 py-1" data-testid="brand-preview-logo" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-white/25 flex items-center justify-center text-white text-base font-bold shrink-0">
                {(publicName || "M").trim().charAt(0).toUpperCase()}
              </div>
            )}
            <span className="font-display font-extrabold text-white text-base truncate" data-testid="brand-preview-name">{publicName || "Tu negocio"}</span>
          </div>
          <div className="p-4 bg-white">
            <p className="text-[11px] uppercase tracking-wide text-neutral-400 font-semibold">Vista previa · así te verán tus participantes</p>
            <div className="mt-2 rounded-lg bg-neutral-50 border border-neutral-100 p-3">
              <p className="text-sm font-semibold text-neutral-800">Rifa de ejemplo 🎁</p>
              <p className="text-xs text-neutral-500 mt-0.5">Organizado por <b style={{ color: brandColor }}>{publicName || "Tu negocio"}</b></p>
            </div>
          </div>
        </div>
        <div className="space-y-5">
          <div>
            <Label>Nombre privado</Label>
            <Input data-testid="settings-private-name" value={user?.name || ""} disabled readOnly className="mt-1.5 rounded-lg bg-neutral-100 text-neutral-500 cursor-not-allowed" />
            <p className="text-xs text-neutral-400 mt-1">Tu nombre de la cuenta (de Google). Solo lo ves tú; no aparece en las páginas públicas ni correos.</p>
          </div>
          <div>
            <Label>Nombre público / Negocio</Label>
            <Input data-testid="settings-name" value={publicName} onChange={(e) => setPublicName(e.target.value)} className="mt-1.5 rounded-lg" placeholder="Ej: Rifas El Trébol" />
            <p className="text-xs text-neutral-400 mt-1">Es el nombre que ven tus participantes en todas las rifas, el landing público y los correos.</p>
          </div>
          <div>
            <Label>Color de marca</Label>
            <div className="flex items-center gap-3 mt-1.5">
              <input type="color" data-testid="settings-color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} className="w-12 h-10 rounded-lg border border-neutral-200" />
              <Input value={brandColor} onChange={(e) => setBrandColor(e.target.value)} className="rounded-lg" />
            </div>
          </div>
          <div>
            <Label>Logo</Label>
            <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="settings-logo-input" />
            <div className="flex items-center gap-3 mt-1.5">
              {brandLogo ? (
                <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-neutral-200 bg-neutral-50 shrink-0">
                  <img src={brandLogo} alt="Logo" className="w-full h-full object-contain" />
                  <button type="button" data-testid="settings-logo-remove" onClick={() => setBrandLogo("")} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X size={12} /></button>
                </div>
              ) : null}
              <Button type="button" variant="outline" data-testid="settings-logo-upload" disabled={uploadingLogo} onClick={() => logoInputRef.current?.click()} className="rounded-lg">
                {uploadingLogo ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Upload size={16} className="mr-1.5" />}
                {brandLogo ? "Cambiar logo" : "Subir logo"}
              </Button>
            </div>
            <p className="text-xs text-neutral-400 mt-2" data-testid="logo-guide">Recomendado: logo horizontal o cuadrado, fondo transparente (PNG), mínimo 200×200 px. Formatos: JPG, PNG o WebP. Se optimiza automáticamente (máx. 5 MB).</p>
          </div>
          <div>
            <Label>Número SINPE Móvil</Label>
            <Input data-testid="settings-sinpe" value={sinpeNumber} onChange={(e) => setSinpeNumber(e.target.value)} placeholder="8888-8888" className="mt-1.5 rounded-lg" />
            <p className="text-xs text-neutral-400 mt-1">El comprador verá este número con un botón para copiarlo al pagar.</p>
          </div>
          <div>
            <Label>Datos de pago (SINPE / cuenta)</Label>
            <Textarea data-testid="settings-payment" value={paymentInfo} onChange={(e) => setPaymentInfo(e.target.value)} className="mt-1.5 rounded-lg" rows={3} placeholder={"Ej: SINPE Móvil 8888-8888 (María Rodríguez)\nBanco Nacional cuenta IBAN CR00..."} />
            <p className="text-xs text-neutral-400 mt-1">Se mostrará al comprador en el checkout para que sepa a dónde pagar.</p>
          </div>
          <div>
            <Label>WhatsApp (con código de país)</Label>
            <Input data-testid="settings-whatsapp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+506 8888 8888" className="mt-1.5 rounded-lg" />
            <p className="text-xs text-neutral-400 mt-1">Aparecerá como botón flotante en la página de compra.</p>
          </div>
          <div>
            <Label>Símbolo de moneda</Label>
            <Input data-testid="settings-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="mt-1.5 rounded-lg w-24" />
          </div>
          <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl">
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
